// apiAuth.js — attach the signed-in user's Cognito ID token to every JarJar API
// call. Installed once (index.js) as a thin wrapper around window.fetch so all
// pages are covered without touching each call site.
//
// * Only requests to the JarJar API origin get the header; Cognito, S3, etc. are
//   untouched.
// * The ID token is renewed with the refresh token shortly before it expires
//   (public client, refresh token valid 1 day), so long-open pages keep working.
// * A 401 from the API with no usable session sends the user back through
//   sign-in (at most once per 30 s, to avoid redirect loops).
import { AUTH_DISABLED, config, getLoginUrl, parseJwt } from './auth';

export const API_ORIGIN = 'https://22tp4u1f2h.execute-api.us-west-2.amazonaws.com';
const STORAGE_KEY = 'jarjar_tokens';
const RENEW_BEFORE_MS = 2 * 60 * 1000;
const REDIRECT_GUARD_KEY = 'jarjar_auth_redirect_at';

function readTokens() {
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
}

function idTokenExpiresAt(tokens) {
  const p = tokens && tokens.id_token ? parseJwt(tokens.id_token) : null;
  return p && p.exp ? p.exp * 1000 : 0;
}

let renewing = null;
function renewTokens(nativeFetch, tokens) {
  if (renewing) return renewing;
  renewing = nativeFetch(`https://${config.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ grant_type: 'refresh_token', client_id: config.clientId, refresh_token: tokens.refresh_token }),
  })
    .then(r => (r.ok ? r.json() : null))
    .then(fresh => {
      if (fresh && fresh.id_token) {
        // Refresh responses omit refresh_token; keep the existing one.
        const merged = { ...tokens, ...fresh, refresh_token: fresh.refresh_token || tokens.refresh_token };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
        return merged;
      }
      return null;
    })
    .catch(() => null)
    .finally(() => { renewing = null; });
  return renewing;
}

async function currentIdToken(nativeFetch) {
  let tokens = readTokens();
  if (!tokens || !tokens.id_token) return null;
  const exp = idTokenExpiresAt(tokens);
  if (exp - Date.now() < RENEW_BEFORE_MS && tokens.refresh_token) {
    const renewed = await renewTokens(nativeFetch, tokens);
    if (renewed) tokens = renewed;
  }
  return idTokenExpiresAt(tokens) > Date.now() ? tokens.id_token : null;
}

function isApiUrl(input) {
  const url = typeof input === 'string' ? input : (input && input.url) || String(input || '');
  return url.startsWith(API_ORIGIN + '/');
}

function sendToLogin() {
  const last = Number(sessionStorage.getItem(REDIRECT_GUARD_KEY) || 0);
  if (Date.now() - last < 30000) return;
  sessionStorage.setItem(REDIRECT_GUARD_KEY, String(Date.now()));
  sessionStorage.removeItem(STORAGE_KEY);
  window.location.href = getLoginUrl();
}

export function installApiAuth({ redirectOn401 = true } = {}) {
  if (typeof window === 'undefined' || !window.fetch || window.fetch.__jarjarApiAuth) return;
  const nativeFetch = window.fetch.bind(window);
  const wrapped = async (input, init) => {
    if (!isApiUrl(input)) return nativeFetch(input, init);
    const token = await currentIdToken(nativeFetch);
    let req = input, opts = init;
    if (token) {
      if (input instanceof Request) {
        req = new Request(input, init);
        req.headers.set('Authorization', token);
        opts = undefined;
      } else {
        const headers = new Headers((init && init.headers) || undefined);
        headers.set('Authorization', token);
        opts = { ...(init || {}), headers };
      }
    }
    const res = await nativeFetch(req, opts);
    if (res.status === 401 && redirectOn401 && !AUTH_DISABLED) sendToLogin();
    return res;
  };
  wrapped.__jarjarApiAuth = true;
  window.fetch = wrapped;
}
